import { useEffect, useRef, useState, type ReactNode } from 'react';
import { copyToClipboard } from '../../lib/clipboard';

// P2b-3 round-1 polish Fix 2: URL variant of CopyableCell. Click on
// the text/anchor opens the URL in a new tab. The copy icon (revealed
// on hover) is the ONLY trigger that copies. Used for asset folder
// URLs, landing URLs, and ad-account URLs on the role grids.
//
// Empty/null href collapses to a non-interactive em-dash placeholder
// (matches CopyableCell's no-value handling).

type Props = {
  // The URL to open + copy. When null/undefined, the cell renders an
  // em-dash placeholder.
  href: string | null | undefined;
  // What to render in the anchor — defaults to href. Use a short
  // "Open" label when the raw URL would clutter the cell.
  display?: ReactNode;
  ariaLabel: string;
  className?: string;
};

const TOAST_MS = 1500;

export function CopyableLinkCell({ href, display, ariaLabel, className }: Props) {
  const [feedback, setFeedback] = useState<'idle' | 'copied' | 'error'>('idle');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  if (typeof href !== 'string' || href.length === 0) {
    return <span className="text-slate-400">—</span>;
  }

  const handleCopy = async () => {
    const ok = await copyToClipboard(href);
    setFeedback(ok ? 'copied' : 'error');
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setFeedback('idle'), TOAST_MS);
  };

  return (
    <span className={`relative inline-flex items-center gap-1 group ${className ?? ''}`}>
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        aria-label={ariaLabel}
        title={href}
        className="text-left text-xs text-blue-600 hover:underline focus:outline-none focus:ring-1 focus:ring-slate-400 rounded truncate min-w-0 max-w-full"
      >
        {display ?? href}
      </a>
      <button
        type="button"
        onClick={handleCopy}
        aria-label={`Copy ${ariaLabel}`}
        title="Copy URL"
        className="text-slate-400 hover:text-slate-700 text-xs opacity-0 group-hover:opacity-100 transition-opacity"
      >
        ⎘
      </button>
      {feedback !== 'idle' && (
        <span
          role="status"
          aria-live="polite"
          className={
            'absolute -top-6 left-0 px-2 py-0.5 text-xs rounded shadow-md whitespace-nowrap ' +
            (feedback === 'copied' ? 'bg-emerald-600 text-white' : 'bg-rose-600 text-white')
          }
        >
          {feedback === 'copied' ? 'Copied!' : 'Copy failed'}
        </span>
      )}
    </span>
  );
}
